import {
  Box,
  List,
  Stack,
  Alert,
  Skeleton,
  ListItem,
  Snackbar,
  SnackbarCloseReason,
} from "@mui/material";
import Text from "src/components/Displays/Text";
import { Trans, useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { evsesCompatibility } from "src/api/smartChargingClients";
import { SESSION_KEYS, EvsesCompatibility } from "src/consts/session";
import { SMART_CHARGING_ROUTES } from "src/consts/Routes/SmartCharging";
import EvseListItem from "src/components/SmartCharging/Components/EvseListItem";

const Compatibility = () => {
  const navigate = useNavigate();
  const [msg, setMsg] = useState("");
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation("translation");

  const [isLoading, setIsLoading] = useState(false);

  const [getCustomerAccess] = useSessionStorage<string>(
    SESSION_KEYS.MFE_CUSTOMER_ACCESS,
  );
  const [, setEvsesCompatibility] = useSessionStorage<EvsesCompatibility[]>(
    SESSION_KEYS.EVSES_COMPATIBILITY,
  );

  const [manufacturersList, setManufacturersList] = useState<string[]>([]);

  const initialCall = useCallback(async () => {
    setIsLoading(true);
    try {
      const customerAccess = getCustomerAccess();
      if (!customerAccess) {
        return;
      }

      const response = await evsesCompatibility(customerAccess);
      setEvsesCompatibility(response);

      const filteredEvsesList = response.filter(
        (evse) => evse.compatibilityStatus === "PREMIUM",
      );
      const manufacturersList = filteredEvsesList.map(
        (item) => item.manufacturer,
      );
      const manufacturersListDistinct = manufacturersList.filter(
        (manufacturer, index, list) => list.indexOf(manufacturer) === index,
      );

      setTimeout(() => {
        setIsLoading(false);
        setManufacturersList(manufacturersListDistinct);
      }, 1000);
    } catch (e) {
      setIsLoading(false);
      const error = e as Error;
      setMsg(error.msgId ?? "");
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    initialCall();
  }, [initialCall]);

  const handleSearchClick = () => {
    navigate(SMART_CHARGING_ROUTES.MANUFACTURER_SEARCH);
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const handleManufacturerClick = (manufacturer: string) => {
    return `${navigate(SMART_CHARGING_ROUTES.MODEL + `?manufacturer=${manufacturer}`)}`;
  };

  return (
    <Stack gap={5}>
      <Stack gap={5}>
        <Text
          text={t("page.wallboxCompatibility.pageTitle")}
          sx={(theme) => ({
            fontWeight: 500,
            fontSize: "32px",
            lineHeight: "40px",
            fontFamily: "officina",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Text
          text={t("page.wallboxCompatibility.pageSubTitle1")}
          sx={(theme) => ({
            fontSize: "14px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />
      </Stack>

      <Stack gap={5}>
        <Text
          text={t("page.wallboxCompatibility.formTitle")}
          sx={(theme) => ({
            fontSize: "24px",
            fontWeight: "500",
            lineHeight: "28px",
            fontFamily: "officina",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Box
          sx={(theme) => ({
            px: "16px",
            borderRadius: "12px",
            bgcolor: theme.palette.white.main,
          })}
        >
          <List>
            {isLoading && (
              <ListItem disablePadding disableGutters>
                <Skeleton
                  sx={{
                    fontSize: "3rem",
                    width: "100%",
                  }}
                />
              </ListItem>
            )}

            {manufacturersList &&
              manufacturersList.map((manufacturer, index) => (
                <EvseListItem
                  key={manufacturer}
                  itemText={manufacturer}
                  divider={index < manufacturersList.length - 1}
                  handleItemClick={() => handleManufacturerClick(manufacturer)}
                />
              ))}
          </List>
        </Box>
      </Stack>

      <Stack gap={2}>
        <Text
          text={t("page.wallboxCompatibility.para1")}
          sx={(theme) => ({
            fontSize: "14px",
            fontWeight: "600",
            lineHeight: "24px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Text
          text={
            <Trans
              i18nKey={
                t(
                  "page.wallboxCompatibility.para1Desc",
                ) as "page.wallboxCompatibility.para1Desc"
              }
              t={t}
              components={{ bold: <strong /> }}
            />
          }
          sx={(theme) => ({
            fontSize: "12px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />
      </Stack>

      <Stack>
        <PrimaryButton
          isLoading={isLoading}
          onClick={handleSearchClick}
          text={"Ich besitze eine andere Wallbox"}
          sx={{
            border: "1px solid #00000038",
            backgroundColor: "transparent",
            color: "#3B3B3B",
          }}
          disabled={msg !== ""}
        />
      </Stack>

      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
        <Alert
          severity="error"
          variant="filled"
          onClose={handleClose}
          sx={{ width: "100%" }}
        >
          {msg}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Compatibility;
